import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Layout, theme, Grid, Badge, Button, Tooltip, Spin, Result } from "antd";
import { Outlet } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAppContext } from "../context/AppContext";
import {
  getThemeSettings,
  getStoredThemeSettings,
  saveStoredThemeSettings,
} from "../services/themeService";
import InvoiceNavigation from "../navigation/InvoiceNavigationContainer";
import { useAuth } from "../context/AuthContext";
import { BellOutlined, LogoutOutlined } from "@ant-design/icons";
import InvoiceNotificationPanel from "../components/InvoiceNotificationPanel";
import { InvoiceAccessProvider, useInvoiceAccess } from "../context/InvoiceAccessContext";
import { InvoiceNotificationProvider, useInvoiceNotifications } from "../context/InvoiceNotificationContext";

const { useBreakpoint } = Grid;

const InvoiceLayoutInner: React.FC = () => {
  const {
    state: { isNavMenuClose },
    appContextAction,
  } = useAppContext();
  const { theme: currentTheme, updateTheme } = useTheme();
  const { user, logout } = useAuth();
  const { unreadCount, refetchNotifications } = useInvoiceNotifications();
  const { access, loading: accessLoading, error: accessError } = useInvoiceAccess();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isSmallDesktop = screens.md && !screens.lg;
  const isDarkMode = currentTheme.algorithm?.[0] === theme.darkAlgorithm;

  const [notificationPanelVisible, setNotificationPanelVisible] = useState(false);

  const hasOpenedInvoiceRef = useRef(false);

  useEffect(() => {
    if (!hasOpenedInvoiceRef.current) {
      appContextAction.app.open("invoice");
      hasOpenedInvoiceRef.current = true;
    }
  }, [appContextAction]);

  const themeTokens = useMemo(
    () => ({
      background: isDarkMode ? "#141414" : "#f5f7fa",
      contentBackground: isDarkMode ? "#1f1f1f" : "#ffffff",
      textColor: isDarkMode
        ? "rgba(255, 255, 255, 0.85)"
        : "rgba(0, 0, 0, 0.85)",
      textColorSecondary: isDarkMode
        ? "rgba(255, 255, 255, 0.45)"
        : "rgba(0, 0, 0, 0.45)",
      borderColor: isDarkMode ? "#303030" : "#f0f0f0",
      primaryColor: currentTheme.token?.colorPrimary || "#1d8348",
    }),
    [isDarkMode, currentTheme.token?.colorPrimary]
  );

  const loadThemeFromAPI = useCallback(async () => {
    try {
      if (getStoredThemeSettings()) {
        return;
      }

      const apiSettings = await getThemeSettings();
      if (apiSettings) {
        saveStoredThemeSettings(apiSettings);
        updateTheme(apiSettings);
      }
    } catch (error) {
      console.error("Failed to load theme settings in InvoiceLayout:", error);
    }
  }, [updateTheme]);

  useEffect(() => {
    if (!getStoredThemeSettings()) {
      loadThemeFromAPI();
    }
  }, [loadThemeFromAPI]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      refetchNotifications();
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refetchNotifications]);

  const handleNotificationClick = () => {
    setNotificationPanelVisible(true);
  };

  const handleNotificationPanelClose = () => {
    setNotificationPanelVisible(false);
  };

  const layoutStyles = useMemo(
    () => ({
      backgroundColor: themeTokens.background,
      minHeight: "100vh",
      height: "100%",
      overflow: "hidden",
      display: "flex",
      color: themeTokens.textColor,
      transition: "all 0.3s ease-in-out",
    }),
    [themeTokens]
  );

  const contentStyles = useMemo(
    () => ({
      marginLeft: isMobile
        ? 0
        : isNavMenuClose
        ? 80
        : isSmallDesktop
        ? 220
        : 260,
      width: "100%",
      padding: isMobile ? "56px 16px 16px" : "0",
      overflow: "auto",
      minHeight: "100vh",
      background: themeTokens.contentBackground,
      transition: "all 0.3s ease-in-out",
      color: themeTokens.textColor,
    }),
    [themeTokens, isNavMenuClose, isMobile, isSmallDesktop]
  );

  const headerStyles = useMemo(
    () => ({
      background: "transparent",
      padding: isMobile ? "8px 12px" : "8px 16px",
      height: 52,
      lineHeight: "52px",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end" as const,
      color: themeTokens.textColor,
      backdropFilter: "none",
    }),
    [isMobile, themeTokens.textColor]
  );

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      isDarkMode ? "dark" : "light"
    );
    document.body.style.backgroundColor = themeTokens.background;
    document.body.style.color = themeTokens.textColor;

    const style = document.createElement("style");
    style.textContent = `
      :root {
        color-scheme: ${isDarkMode ? "dark" : "light"};
        --primary-color: ${themeTokens.primaryColor};
        --background-color: ${themeTokens.background};
        --content-background: ${themeTokens.contentBackground};
        --text-color: ${themeTokens.textColor};
        --text-color-secondary: ${themeTokens.textColorSecondary};
        --border-color: ${themeTokens.borderColor};
      }

      .ant-card,
      .ant-modal .ant-modal-content,
      .ant-popover .ant-popover-inner,
      .ant-dropdown .ant-dropdown-menu,
      .ant-select-dropdown {
        background: var(--content-background) !important;
      }

      .ant-card-bordered,
      .ant-modal-content,
      .ant-popover-inner {
        border-color: var(--border-color) !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [isDarkMode, themeTokens]);

  return (
    <Layout style={layoutStyles}>
      <InvoiceNavigation />
      <Layout style={contentStyles}>
        <Layout.Header style={headerStyles}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Tooltip title="Notifications">
              <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                <Button
                  type="text"
                  shape="circle"
                  size="small"
                  icon={<BellOutlined />}
                  onClick={handleNotificationClick}
                />
              </Badge>
            </Tooltip>
            <Tooltip title="Logout">
              <Button
                type="text"
                shape="circle"
                size="small"
                icon={<LogoutOutlined />}
                onClick={logout}
              />
            </Tooltip>
            <div style={{ fontWeight: 600 }}>{user?.name || "User"}</div>
          </div>
        </Layout.Header>
        <Layout.Content>
          {accessLoading ? (
            <div
              style={{
                minHeight: "calc(100vh - 52px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Spin size="large" />
            </div>
          ) : accessError ? (
            <Result
              status="error"
              title="Unable to load access"
              subTitle={accessError}
            />
          ) : access?.readAccess ? (
            <Outlet />
          ) : (
            <Result
              status="403"
              title="Access restricted"
              subTitle="You do not have permission to access the Invoice Portal."
            />
          )}
        </Layout.Content>
      </Layout>

      <InvoiceNotificationPanel
        visible={notificationPanelVisible}
        onClose={handleNotificationPanelClose}
      />
    </Layout>
  );
};

const InvoiceLayout: React.FC = () => {
  return (
    <InvoiceNotificationProvider>
      <InvoiceAccessProvider>
        <InvoiceLayoutInner />
      </InvoiceAccessProvider>
    </InvoiceNotificationProvider>
  );
};

export default React.memo(InvoiceLayout);
